import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { getBillingMode, startCheckout } from "@/lib/billing.functions";
import { reservePaymentWindow, goToPayment, closePaymentWindow } from "@/lib/open-payment";

const AuthModal = lazy(() =>
  import("@/components/auth/AuthModal").then((m) => ({ default: m.AuthModal })),
);
import {
  Check,
  Sparkles,
  Bug,
  ChevronDown,
  CreditCard,
  Copy,
  ShieldCheck,
  Lock,
  EyeOff,
  MessageSquare,
} from "lucide-react";
import {
  FAIR_USE_NOTE,
  PLAN_DISCLAIMER,
  PLANS,
  PLAN_ORDER,
  yearlySavingSek,
  type PlanId,
} from "@/lib/plans";

import { useAuth } from "@/hooks/useAuth";
import { myPlanQuery, myRoleQuery } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { useAppPlatform } from "@/app-bridge/core/platform";
import { fillSurface, useSurfaceWords } from "@/lib/surface";
import { PlanCostBreakdown } from "@/components/admin/PlanCostBreakdown";
import { getAppScroller } from "@/lib/scroll";
import { FAQ, localizedPricingFaq } from "@/lib/pricing-faq";
import { useExpertFeatures } from "@/hooks/useExpertFeatures";
import { useLocale } from "@/lib/i18n/locale-context";
import { localizedPath } from "@/lib/i18n/slug-map";
import { localizedPlan, pricingCopy } from "@/lib/i18n/pricing-copy";


export const Route = createFileRoute("/priser")({
  head: () => ({
    meta: [
      { title: "Priser – gratis, Plus 59 kr och Pro 139 kr/mån | Sellavo" },
      {
        name: "description",
        content:
          "Lägg ut annonser gratis (10 per månad), Plus 59 kr/mån för 60 annonser eller Pro 139 kr/mån för obegränsat. Årsvis ger 2 månader gratis. Alla priser inkl. moms.",
      },
      { property: "og:title", content: "Priser – gratis, Plus 59 kr och Pro 139 kr/mån | Sellavo" },
      {
        property: "og:description",
        content:
          "Sälj dina prylar gratis, eller välj Plus 59 kr eller Pro 139 kr i månaden – jämför gränser, funktioner och årsrabatt.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sellavo.com/priser" },
      { name: "twitter:card", content: "summary" },
    ],

    links: [{ rel: "canonical", href: "https://sellavo.com/priser" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
    ],
  }),
  component: Pricing,
});


const TRUST = [
  {
    icon: EyeOff,
    title: "Din adress syns aldrig publikt",
    body: "Annonsen visar bara orten och ett ungefärligt område på kartan. Exakt adress, telefonnummer och e-post visas inte för andra användare.",
  },
  {
    icon: MessageSquare,
    title: "Kontakt sker inne {på}",
    body: "Köpare hör av sig via meddelanden här, så du slipper lämna ut dina kontaktuppgifter för att sälja något.",
  },
  {
    icon: Lock,
    title: "Du styr över dina annonser",
    body: "Du kan pausa, ändra, arkivera eller ta bort en annons när du vill, oavsett vilket konto du har.",
  },
  {
    icon: ShieldCheck,
    title: "Inga dolda avgifter eller bindningstid",
    body: "Ingen bindningstid och ingen provision. Du betalar bara för den period du valt och kan byta nivå eller sluta betala när som helst.",
  },
];

/**
 * Testkortet visas bara för admin när betalningarna körs i testläge,
 * så att man kan gå igenom hela köpet utan riktiga pengar.
 */
const TEST_CARD = [
  { label: "Kortnummer", value: "4242 4242 4242 4242" },
  { label: "Utgångsdatum", value: "12/34" },
  { label: "CVC", value: "123" },
  { label: "Postnummer", value: "12345" },
];

function TestCardBox() {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value.replace(/\s/g, ""));
      setCopied(value);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Kunde inte kopiera.");
    }
  };

  return (
    <div className="mt-6 rounded-md border border-dashed border-amber-500/60 bg-amber-500/10 p-4">
      <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <CreditCard className="h-4 w-4 text-amber-500" /> Testläge – inga riktiga pengar dras
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        Bara du som admin ser den här rutan. Använd kortet nedan i kassan för att testa hela köpet.
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {TEST_CARD.map((item) => (
          <button
            key={item.label}
            type="button"
            onClick={() => copy(item.value)}
            className="flex items-center justify-between gap-3 rounded-md border border-border bg-card px-3 py-2 text-left transition hover:border-primary/50"
          >
            <span className="min-w-0">
              <span className="block text-xs text-muted-foreground">{item.label}</span>
              <span className="block font-mono text-sm font-semibold text-foreground">
                {item.value}
              </span>
            </span>
            {copied === item.value ? (
              <Check className="h-4 w-4 shrink-0 text-green-500" />
            ) : (
              <Copy className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
          </button>
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Valfritt namn och valfri e-post fungerar. Vill du testa ett nekat kort använder du
        4000 0000 0000 0002.
      </p>
    </div>
  );
}

export function Pricing() {
  const { lang } = useLocale();
  const pc = lang === "sv" ? null : pricingCopy[lang];
  const pageFaq = localizedPricingFaq(lang);
  const trustItems = pc?.trustItems?.map((item, index) => ({ ...TRUST[index], ...item })) ?? TRUST;
  const { enabled: testFeatures, testOnly: expertTestOnly } = useExpertFeatures();
  const visiblePlans = testFeatures ? PLAN_ORDER : PLAN_ORDER.filter((id) => id !== "expert");
  // Sidan ska alltid börja högst upp – oavsett var man kom ifrån och
  // oavsett om det är mobilens egen rullningsyta eller fönstret som scrollar.
  useEffect(() => {
    const scroller = getAppScroller();
    if (scroller) scroller.scrollTo({ top: 0 });
    else window.scrollTo({ top: 0 });
  }, []);

  const words = useSurfaceWords();
  const { user } = useAuth();
  const platform = useAppPlatform();


  const { data: roleData } = useQuery({ ...myRoleQuery(), enabled: !!user });
  const { data: planStatus } = useQuery({ ...myPlanQuery(), enabled: !!user });
  const [debugPlan, setDebugPlan] = useState<PlanId | null>(null);
  const [billing, setBilling] = useState<"month" | "year">("year");
  const isSuperadmin = roleData?.role === "superadmin";
  const isAdmin = isSuperadmin || roleData?.role === "admin";
  const activePlan: PlanId | null = debugPlan ?? (planStatus?.plan as PlanId | undefined) ?? null;
  const currentPlan: PlanId = (planStatus?.plan as PlanId | undefined) ?? "free";

  const [checkoutPlan, setCheckoutPlan] = useState<PlanId | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<{ plan: PlanId; url: string } | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const checkout = useServerFn(startCheckout);
  const billingMode = useServerFn(getBillingMode);
  const { data: modeData } = useQuery({
    queryKey: ["billing-mode"],
    queryFn: () => billingMode({}),
    enabled: !!user && isAdmin,
    staleTime: 5 * 60 * 1000,
  });
  const showTestCard = isAdmin && modeData?.testMode === true;

  const handleCheckout = async (id: PlanId) => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    // Öppna fliken synkront från klicket. Stripe tillåter inte alltid att
    // Checkout visas inuti den inbäddade Lovable-förhandsvisningen, och en
    // window.open efter await blockeras ofta som popup av webbläsaren.
    const paymentWindow = reservePaymentWindow();
    setCheckoutUrl(null);
    setCheckoutPlan(id);
    try {
      const { url } = await Promise.race([
        checkout({
          data: {
            plan: id as "plus" | "pro" | "expert",
            interval: billing,
            returnOrigin: window.location.origin,
          },
        }),
        new Promise<never>((_, reject) =>
          window.setTimeout(
            () => reject(new Error("Stripe svarade inte. Försök igen.")),
            20_000,
          ),
        ),
      ]);
      setCheckoutUrl({ plan: id, url });
      const opened = goToPayment(url, paymentWindow);
      if (!opened) {
        toast("Din webbläsare blockerade betalningsfönstret.", {
          duration: 60000,
          action: { label: "Öppna betalningen", onClick: () => window.open(url, "_blank") },
        });
      }
    } catch (err) {
      closePaymentWindow(paymentWindow);
      toast.error(err instanceof Error ? err.message : "Betalningen kunde inte startas.");
    } finally {
      setCheckoutPlan(null);
    }
  };

  return (
    <main className="bg-background pb-16 sm:pb-20">

      <section className="bg-ocean-900 px-4 py-10 text-white sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-ocean-300">
             {pc?.eyebrow ?? "Konton och nivåer"}
          </p>
          <h1 className="mt-3 font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
             {pc?.title ?? "Priser"}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-ocean-100 sm:mt-5 sm:text-lg">
             {pc?.intro ?? "Alla konton får samma hjälp av oss med att skriva annonsen. Skillnaden är hur mycket du får göra. Exakt vad som ingår står under varje konto, så att du vet vad du betalar för."}
          </p>
          <div className="mt-5 inline-flex items-center gap-2 rounded-lg border border-white/25 bg-white/10 px-3 py-2 text-sm font-semibold text-white">
            <Sparkles className="h-4 w-4" />
             {pc?.yearlyDeal ?? "Årsvis betalning = 2 månader gratis"}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 pt-6 sm:px-6 sm:pt-10 lg:px-8">

      {showTestCard && <TestCardBox />}




      {isSuperadmin && (
        <div className="mt-6 rounded-md border border-dashed border-primary/50 bg-ocean-100/40 p-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Bug className="h-4 w-4 text-primary" /> Debugläge (superadmin)
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Ditt riktiga konto:{" "}
            <strong>{PLANS[(planStatus?.plan as PlanId) ?? "free"].name}</strong>. Förhandsvisa hur
            sidan ser ut för en annan nivå.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {visiblePlans.map((id) => (
              <Button
                key={id}
                size="sm"
                variant={debugPlan === id ? "default" : "outline"}
                onClick={() => setDebugPlan(debugPlan === id ? null : id)}
              >
                Visa som {PLANS[id].name}
              </Button>
            ))}
          </div>
        </div>
      )}
      </div>

      <section className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">

      <div className="mt-8 flex w-full flex-col gap-2 sm:inline-flex sm:w-auto sm:flex-row sm:items-center">
        <div className="flex rounded-md border border-border bg-card p-1">
          <button
            type="button"
            onClick={() => setBilling("month")}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition sm:flex-none sm:px-4 ${
              billing === "month"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
             {pc?.monthly ?? "Månadsvis"}
          </button>
          <button
            type="button"
            onClick={() => setBilling("year")}
            className={`relative flex-1 rounded-md px-3 py-2 text-sm font-semibold transition sm:flex-none sm:px-4 ${
              billing === "year"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
             {pc?.yearly ?? "Årsvis"}
            <span className="absolute -right-1 -top-2.5 rotate-3 rounded-full bg-green-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm sm:-right-2 sm:-top-3">
               {pc?.saveTwo ?? "Spara 2 mån"}
            </span>
          </button>
        </div>
        {billing === "year" && (
          <p className="text-center text-xs font-medium text-green-500 sm:text-left">
             {pc?.yearlyNote ?? "Betala för 10 månader – få 12 månader. Du sparar upp till 278 kr/år."}
          </p>
        )}
      </div>

      <div className="mt-8 grid gap-4 sm:gap-5 md:mt-10 md:grid-cols-2 xl:grid-cols-4">
        {visiblePlans.map((id) => {
           const plan = PLANS[id];
           const translatedPlan = localizedPlan(lang, id);
           const planName = translatedPlan?.name ?? plan.name;
          const isCurrent = activePlan === id;
          const isPopular = !activePlan && id === "plus";
          const currentIndex = activePlan ? PLAN_ORDER.indexOf(activePlan) : -1;
          const thisIndex = PLAN_ORDER.indexOf(id);
          const isDowngrade = currentIndex > -1 && thisIndex < currentIndex;
          return (
            <div
              key={id}
              className={`relative flex min-w-0 flex-col rounded-lg border p-5 sm:p-6 ${
                isCurrent
                  ? "border-primary bg-card ring-2 ring-primary shadow-md"
                  : isPopular
                    ? "border-primary/60 bg-card shadow-sm"
                    : "border-border bg-card"
              }`}
            >
              {(isCurrent || isPopular || (id === "expert" && expertTestOnly)) && (
                <div className="mb-3 flex flex-wrap gap-2 xl:absolute xl:right-4 xl:top-4 xl:mb-0">
                  {id === "expert" && expertTestOnly && (
                    <span className="rounded-full bg-amber-500/15 px-2 py-1 text-xs font-bold text-amber-700 dark:text-amber-300">
                      Test
                    </span>
                  )}
                  {(isCurrent || isPopular) && (
                    <span
                      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold ${
                        isCurrent ? "bg-primary text-primary-foreground" : "bg-primary/15 text-primary"
                      }`}
                    >
                      <Sparkles className="h-3.5 w-3.5" /> {isCurrent ? (pc?.current ?? "Ditt konto") : (pc?.popular ?? "Populärast")}
                    </span>
                  )}
                </div>
              )}
              {/* Rubrikzon: samma höjd i alla kort så priserna hamnar på samma linje. */}
              <div className="min-w-0 xl:min-h-[4.5rem]">
                <h2 className="break-words font-heading text-xl font-bold text-foreground xl:pr-28">
                   {planName}
                </h2>
                 <p className="mt-1 text-sm text-muted-foreground">{translatedPlan?.tagline ?? plan.tagline}</p>
              </div>
              {/* Priszon: extra rader visas bara vid årsvis, då de behövs för linjeringen. */}
              <div className="mt-4">
                <p className="font-heading text-3xl font-extrabold text-foreground">
                  {billing === "year" && plan.priceSek > 0 ? plan.priceYearLabel : plan.priceLabel}
                </p>
                {billing === "year" && (
                  <div className="mt-1 min-h-7">
                    {plan.priceSek > 0 ? (
                      <p className="inline-flex items-center gap-1.5 rounded-full bg-green-500/10 px-2 py-0.5 text-sm font-semibold text-green-500">
                         {pc?.save ?? "Spara"} {yearlySavingSek(plan)} kr/{pc?.perYear ?? "år"}
                      </p>
                    ) : (
                      <p className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2 py-0.5 text-sm font-semibold text-primary">
                         {pc?.alwaysFree ?? "Alltid gratis"}
                      </p>
                    )}
                  </div>
                )}
                {billing === "year" && (
                  <div className="mt-2 min-h-10 text-sm text-muted-foreground">
                    {plan.priceSek > 0 ? (
                      <p>
                         {pc?.equivalent ?? "Motsvarar"} {Math.round(plan.priceYearSek / 12)} kr/{pc?.perMonth ?? "mån. Du betalar för 10 månader och får 12."}
                      </p>
                    ) : (
                       <p>{pc?.noCardLong ?? "Inget kort och ingen bindningstid – du betalar aldrig något."}</p>
                    )}
                  </div>
                )}
                <p className="mt-2 text-xs text-muted-foreground">
                   {plan.priceSek > 0 ? (pc?.vat ?? "Inklusive moms (25 %)") : (pc?.noCard ?? "Inget kort behövs")}
                </p>
              </div>
              <ul className="mt-5 space-y-2 text-sm">
                 {(translatedPlan?.features ?? plan.features.map((f) => f.text)).map((text, index) => (
                   <li key={text} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span className="text-foreground/85">
                       {text}
                       {plan.features[index]?.fairUse && (
                        <span className="ml-0.5 align-super text-xs font-semibold text-primary">
                          *
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Exakta gränser + knapp förankras mot kortets botten så korten linjerar. */}
              <div className="mt-auto pt-4">
                <details className="group rounded-lg">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-2 rounded-lg border border-border bg-background/60 px-3 py-2.5 text-sm font-semibold text-foreground transition hover:border-primary/50 hover:text-primary">
                     <span>{pc?.limits ?? "Exakta gränser"}</span>
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
                  </summary>
                  <ul className="mt-2 space-y-1 px-3 text-xs leading-relaxed text-muted-foreground">
                     {(translatedPlan?.notes ?? plan.notes).map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                </details>

                {isAdmin && <PlanCostBreakdown planId={id} />}
              </div>

              <div className="pt-4">


                {isCurrent ? (
                  <Button variant="default" className="w-full" disabled>
                     {pc?.currentLevel ?? "Nuvarande nivå"}
                  </Button>
                ) : isDowngrade && id === "free" ? (
                  <Button variant="outline" className="w-full" disabled>
                     {pc?.downgradeFree ?? "Nedgradera till Gratis"}
                  </Button>
                ) : isDowngrade ? (
                  <Button variant="outline" className="w-full" disabled>
                     {pc?.downgrade ?? "Nedgradera till"} {planName}
                  </Button>
                ) : id === "free" ? (
                  <Button asChild variant="outline" className="w-full">
                     <Link to={localizedPath("/skapa", lang)}>{pc?.createFree ?? "Skapa annons gratis"}</Link>
                  </Button>
                ) : platform !== "web" ? (
                  // Apple och Google tillåter inte att digitala nivåer säljs
                  // eller länkas ut från appen – här visas bara information.
                  <p className="rounded-lg border border-border bg-muted/40 p-3 text-center text-xs text-muted-foreground">
                     {pc?.appPlans ?? "Nivåer hanteras utanför appen. Du kan fortsätta använda allt du redan har här."}
                  </p>
                ) : checkoutUrl?.plan === id ? (
                  <Button asChild className="w-full">
                    <a href={checkoutUrl.url} target="_blank" rel="noopener noreferrer">
                       {pc?.checkout ?? "Öppna kassan"}
                    </a>
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    disabled={checkoutPlan !== null}
                    onClick={() => handleCheckout(id)}
                  >
                     {checkoutPlan === id ? (pc?.opening ?? "Öppnar betalning…") : `${pc?.choose ?? "Välj"} ${planName}`}
                  </Button>
                )}
                {isDowngrade && (
                  <p className="mt-2 text-xs text-muted-foreground">
                     {pc?.downgradeNote ?? "Nedgradering börjar gälla när din nuvarande period tar slut – du behåller det du betalat för."}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Fotnoterna samlas en gång, i stället för i varje kort. */}
      <div className="mt-5 space-y-2 text-xs leading-relaxed text-muted-foreground">
        <p>
           <span className="font-semibold text-primary">*</span> {pc?.fair ?? FAIR_USE_NOTE}
        </p>
         <p>{pc?.disclaimer ?? PLAN_DISCLAIMER}</p>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-4 sm:p-5">
        <div className="min-w-0">
          <p className="font-heading text-base font-semibold text-foreground">
             {pc?.businessTitle ?? "Säljer du som företag?"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
             {pc?.businessBody ?? "Företag är ingen egen nivå – du slår på företagskonto på ditt konto och väljer Plus eller Pro som vanligt. Då får du org.nr på kvittot, ”Företag”-märkning på annonserna och en publik företagsprofil."}
          </p>
        </div>
        <Button asChild variant="outline">
           <Link to={localizedPath("/konto", lang)} hash="foretagskonto">
             {pc?.businessButton ?? "Slå på företagskonto"}
          </Link>
        </Button>
      </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">

       <p className="mt-8 text-sm text-muted-foreground">
         {pc?.details ?? <>Alla priser är inklusive moms. Pausade annonser räknas fortfarande som skapade den månad de
        lades upp. Du kan pausa och återaktivera hur ofta du vill under{" "}
        <Link to="/mina-annonser" className="underline underline-offset-2">
          Mina annonser
        </Link>
        . Där något står som obegränsat gäller det inom rimlig användning, och de exakta gränserna
        står under varje konto, i{" "}

        <Link to="/regler" className="underline underline-offset-2">
          reglerna för tjänsten
        </Link>{" "}
        och i användarvillkoren (Juridik & cookies längst ner på sidan). Vill du veta hur videoskanningen fungerar hittar du allt på{" "}
        <Link to="/videoskanning" className="underline underline-offset-2">
          sidan om videoskanning
        </Link>
        .</>}
      </p>


      <div className="mt-10 rounded-lg border border-border bg-card p-6">
        <h2 className="font-heading text-lg font-semibold text-foreground">
           {pc?.why ?? "Varför kostar fler annonser något?"}
        </h2>
        <p className="mt-2 text-muted-foreground">
           {pc?.whyBody ?? "Vi granskar bilderna, skriver annonstexten och föreslår pris åt dig – det är det som gör att du kan lägga upp en annons på under en minut. Gratisnivån räcker för dig som rensar då och då. Säljer du ofta hjälper Plus- eller Pro-abonnemanget oss att täcka kostnaden för teknik, lagring och drift."}
        </p>
        <Button asChild variant="outline" className="mt-4">
           <Link to={localizedPath("/varfor-kostar-det", lang)}>{pc?.read ?? "Läs mer om hur vi tänker"}</Link>
        </Button>
      </div>

      <section className="mt-12">
         <h2 className="font-heading text-2xl font-bold text-foreground">{pc?.faq ?? "Vanliga frågor"}</h2>
        <p className="mt-2 text-sm text-muted-foreground sm:text-base">
           {pc?.faqIntro ?? "Kort om hur granskningen och abonnemanget fungerar, innan du bestämmer dig."}
        </p>
        <div className="mt-6 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
           {pageFaq.map((item) => (
            <details key={item.q} className="group px-5 py-4 sm:px-6">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left font-heading text-base font-semibold text-foreground">
                <span className="min-w-0">{item.q}</span>
                <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      <section className="mt-10 rounded-lg border border-border bg-card p-5 sm:p-6">
        <h2 className="font-heading text-lg font-semibold text-foreground">
           {pc?.trust ?? "Så tar vi hand om dig och dina uppgifter"}
        </h2>
        <ul className="mt-4 space-y-3">
           {trustItems.map((item) => (
            <li key={item.title} className="flex items-start gap-3">
              <item.icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="min-w-0">
                <p className="font-semibold text-foreground">{fillSurface(item.title, words)}</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
              </div>
            </li>
          ))}
        </ul>
         <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
           {pc?.trustNote ?? <>Den här sidan underhålls av oss som driver Sellavo och beskriver hur tjänsten fungerar i
          dag. Den är inte en certifiering eller en extern granskning. Har du en fråga om dina
          uppgifter eller vill anmäla något säkerhetsrelaterat når du oss via{" "}
          <Link to="/om" className="underline underline-offset-2">
            kontaktuppgifterna på Om-sidan
          </Link>
           .</>}
        </p>
      </section>
      </div>

      {authOpen && (
        <Suspense fallback={null}>
          <AuthModal open onOpenChange={setAuthOpen} />
        </Suspense>
      )}
    </main>

  );
}
